/* eslint-disable */
import { usd, pct, C } from "../ui/tokens";

// ─── REPORT UTILITIES ────────────────────────────────────────────────────────
function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function drawProjectionChart(projections, arbProjections, growthRate, arbitrageRate) {
  const W = 680, H = 270;
  const PAD = { top: 36, right: 64, bottom: 44, left: 72 };
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(0, 0, W, H);

  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  if (!projections || !projections.length) {
    ctx.fillStyle = "#94a3b8"; ctx.font = "13px Arial"; ctx.textAlign = "center";
    ctx.fillText("No projection data", W / 2, H / 2);
    return canvas.toDataURL("image/png");
  }

  const iraVals = projections.map(r => r.NonAnnuityValue + r.AnnuityFMV);
  const rmdVals = projections.map(r => r.TotalRMD);
  const addlVals = projections.map(r => r.AdditionalRMD);
  const arbRMD = (arbProjections || projections).map(r => r.TotalRMD);

  const maxIRA = Math.max(...iraVals, 1);
  const maxRMD = Math.max(...rmdVals, ...addlVals, ...arbRMD, 1);
  const n = projections.length;

  const xPos = i => PAD.left + (i / (n - 1)) * plotW;
  const yL = v => PAD.top + plotH - Math.min(1, v / maxIRA) * plotH;
  const yR = v => PAD.top + plotH - Math.min(1, v / maxRMD) * plotH;

  // Grid
  ctx.strokeStyle = "#e2e8f0"; ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const y = PAD.top + (plotH * i) / 4;
    ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(PAD.left + plotW, y); ctx.stroke();
  }

  // Draw line helper
  const drawLine = (vals, yFn, color, dashed) => {
    ctx.strokeStyle = color; ctx.lineWidth = 2;
    ctx.setLineDash(dashed ? [5, 3] : []);
    ctx.beginPath();
    vals.forEach((v, i) => i === 0 ? ctx.moveTo(xPos(i), yFn(v)) : ctx.lineTo(xPos(i), yFn(v)));
    ctx.stroke();
    ctx.setLineDash([]);
  };

  drawLine(iraVals, yL, "#1e3a5f", false);
  drawLine(rmdVals, yR, "#e11d48", true);
  drawLine(addlVals, yR, "#059669", true);
  if (arbProjections) drawLine(arbRMD, yR, "#3b82f6", true);

  // X axis labels
  ctx.fillStyle = "#64748b"; ctx.font = "10px Arial"; ctx.textAlign = "center";
  projections.forEach((r, i) => {
    if (i === 0 || i === n - 1 || i % 2 === 0)
      ctx.fillText(r.Year, xPos(i), H - PAD.bottom + 14);
  });

  // Left Y axis
  ctx.textAlign = "right";
  for (let i = 0; i <= 4; i++) {
    const v = maxIRA * (4 - i) / 4;
    const y = PAD.top + (plotH * i) / 4;
    const lbl = v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}K`;
    ctx.fillText(lbl, PAD.left - 5, y + 4);
  }

  // Right Y axis
  ctx.textAlign = "left";
  for (let i = 0; i <= 4; i++) {
    const v = maxRMD * (4 - i) / 4;
    const y = PAD.top + (plotH * i) / 4;
    const lbl = v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}K`;
    ctx.fillText(lbl, PAD.left + plotW + 5, y + 4);
  }

  // Legend
  const legend = [
    { color: "#1e3a5f", dashed: false, label: "IRA Value" },
    { color: "#e11d48", dashed: true, label: "Total RMD" },
    { color: "#059669", dashed: true, label: "Add'l RMD" },
    ...(arbProjections ? [{ color: "#3b82f6", dashed: true, label: "Arb RMD" }] : []),
  ];
  legend.forEach((l, i) => {
    const lx = PAD.left + i * 150;
    const ly = 14;
    ctx.strokeStyle = l.color; ctx.lineWidth = 1.5;
    ctx.setLineDash(l.dashed ? [4, 2] : []);
    ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(lx + 20, ly); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#64748b"; ctx.font = "10px Arial"; ctx.textAlign = "left";
    ctx.fillText(l.label, lx + 24, ly + 4);
  });

  // Assumptions
  ctx.fillStyle = "#94a3b8"; ctx.font = "9px Arial"; ctx.textAlign = "right";
  ctx.fillText(
    `Growth Rate: ${(growthRate * 100).toFixed(1)}%   Arbitrage Rate: ${(arbitrageRate * 100).toFixed(1)}%`,
    W - PAD.right, H - 4
  );

  return canvas.toDataURL("image/png");
}

function downloadEML(client, rmdResult, projData, growthRate, arbitrageRate, logoSrc) {
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const clientName = `${client.first_name} ${client.last_name}`;
  const chartDataUrl = projData
    ? drawProjectionChart(projData.projections, projData.arbProjections, growthRate, arbitrageRate)
    : null;

  const rmdRows = rmdResult && !rmdResult.notRequired ? `
    <tr><td>Age at Year End</td><td><b>${rmdResult.ageYE}</b></td></tr>
    <tr><td>Distribution Divisor</td><td><b>${rmdResult.divisor?.toFixed(1)}</b></td></tr>
    <tr><td>Regular IRA Total</td><td><b>${usd(rmdResult.regularTotal)}</b></td></tr>
    <tr><td>Annuity FMV Total</td><td><b>${usd(rmdResult.annuityFMVTotal)}</b></td></tr>
    <tr><td>Total FMV</td><td><b>${usd(rmdResult.totalFMV)}</b></td></tr>
    <tr><td>Total Required RMD</td><td><b>${usd(rmdResult.totalRMD)}</b></td></tr>
    <tr><td>Annuity Income</td><td><b style="color:#059669">${usd(rmdResult.annuityIncome)}</b></td></tr>
    <tr><td>Additional RMD Needed</td><td><b style="color:${rmdResult.additionalRMD > 0 ? "#e11d48" : "#059669"}">${usd(rmdResult.additionalRMD)}</b></td></tr>
  ` : rmdResult?.notRequired
    ? `<tr><td colspan="2">RMD not yet required. Begins at age ${rmdResult.rmdAge} (${rmdResult.yearsUntil} yr away).</td></tr>`
    : `<tr><td colspan="2">RMD not calculated.</td></tr>`;

  const projRows = projData ? projData.projections.map(r => `
    <tr>
      <td>${r.Year}</td><td>${r.AgeYE}</td>
      <td>${usd(r.NonAnnuityValue + r.AnnuityFMV)}</td>
      <td>${r.Divisor.toFixed(1)}</td>
      <td>${usd(r.TotalRMD)}</td>
      <td>${usd(r.AnnuityIncome)}</td>
      <td style="color:${r.AdditionalRMD > 0 ? "#e11d48" : "#059669"}">${usd(r.AdditionalRMD)}</td>
    </tr>`).join("") : `<tr><td colspan="7">Projections not calculated.</td></tr>`;

  const tdS = `style="padding:6px 10px;border:1px solid #e2e8f0;font-size:12px;"`;
  const thS = `style="padding:7px 10px;background:#1e3a5f;color:#fff;font-size:11px;text-align:left;"`;

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"></head>
<body style="font-family:Arial,sans-serif;color:#0f172a;max-width:720px;margin:0 auto;padding:24px;">
  <div style="text-align:center;margin-bottom:24px;border-bottom:2px solid #1e3a5f;padding-bottom:20px;">
    <img src="${logoSrc}" style="height:56px;object-fit:contain;" alt="Harness Financial"/>
    <h1 style="color:#1e3a5f;font-size:20px;margin:12px 0 2px;">Sec. 204 Calculations</h1>
    <div style="color:#64748b;font-size:13px;">Prepared for <b>${clientName}</b> &bull; ${today}</div>
    ${client.contact_address ? `<div style="color:#64748b;font-size:12px;margin-top:4px;">${client.contact_address}</div>` : ""}
  </div>

  <h2 style="color:#1e3a5f;font-size:15px;margin:0 0 10px;">RMD Calculation</h2>
  <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
    <tbody style="font-family:monospace;">
      ${rmdRows}
    </tbody>
  </table>

  <h2 style="color:#1e3a5f;font-size:15px;margin:0 0 6px;">10-Year Projections</h2>
  <div style="font-size:11px;color:#64748b;margin-bottom:10px;">
    Growth Rate: <b>${(growthRate * 100).toFixed(1)}%</b> &nbsp;&bull;&nbsp; Arbitrage Rate: <b>${(arbitrageRate * 100).toFixed(1)}%</b>
  </div>
  ${chartDataUrl ? `<img src="${chartDataUrl}" style="width:100%;max-width:680px;border-radius:8px;margin-bottom:16px;" alt="10-Year Projection Chart"/>` : ""}
  <table style="width:100%;border-collapse:collapse;margin-bottom:24px;font-size:12px;">
    <thead>
      <tr>
        <th ${thS}>Year</th><th ${thS}>Age</th><th ${thS}>IRA Value</th>
        <th ${thS}>Divisor</th><th ${thS}>Total RMD</th>
        <th ${thS}>Ann. Income</th><th ${thS}>Add'l RMD</th>
      </tr>
    </thead>
    <tbody>${projRows}</tbody>
  </table>

  <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;font-size:10px;color:#94a3b8;line-height:1.6;">
    This report is for educational and planning purposes only. Actual RMD amounts depend on year-end account values, IRS table updates, and individual circumstances. Consult a qualified tax professional before making distribution decisions.
    Securities and advisory services offered through LPL Financial, a registered investment advisor, Member FINRA/SIPC.
    Section 204 Plan is a trademark and patent pending of Harness Financial, LLC.
  </div>
</body></html>`;

  const toAddr = client.contact_email || "";
  const eml = [
    "MIME-Version: 1.0",
    `Date: ${new Date().toUTCString()}`,
    `Subject: Sec. 204 Calculations`,
    `To: ${toAddr}`,
    'Content-Type: text/html; charset="utf-8"',
    "",
    html,
  ].join("\r\n");

  const blob = new Blob([eml], { type: "message/rfc822" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Sec204_${client.last_name}_${client.first_name}.eml`;
  a.click();
}

async function downloadDOCX(client, rmdResult, projData, growthRate, arbitrageRate, logoSrc) {
  let mod;
  try { mod = await import("docx"); }
  catch { alert("Please run: npm install docx"); return; }

  const {
    Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
    ImageRun, AlignmentType, WidthType, BorderStyle, ShadingType, VerticalAlign,
  } = mod;

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const clientName = `${client.first_name} ${client.last_name}`;

  // Logo image bytes
  const logoBase64 = logoSrc.includes(",") ? logoSrc.split(",")[1] : logoSrc;
  const logoBytes = base64ToUint8Array(logoBase64);

  // Chart image bytes
  let chartBytes = null;
  if (projData) {
    const chartDataUrl = drawProjectionChart(projData.projections, projData.arbProjections, growthRate, arbitrageRate);
    chartBytes = base64ToUint8Array(chartDataUrl.split(",")[1]);
  }

  const border = { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" };
  const borders = { top: border, bottom: border, left: border, right: border };
  const noBorders = { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE } };
  const cellM = { top: 80, bottom: 80, left: 120, right: 120 };

  const hdrCell = (text, w) => new TableCell({
    borders, width: { size: w, type: WidthType.DXA },
    shading: { fill: "1e3a5f", type: ShadingType.CLEAR },
    margins: cellM,
    children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: "FFFFFF", size: 20, font: "Arial" })] })],
  });
  const dataCell = (text, w, color) => new TableCell({
    borders, width: { size: w, type: WidthType.DXA }, margins: cellM,
    children: [new Paragraph({ children: [new TextRun({ text: String(text ?? "—"), size: 20, font: "Arial", color: color || "000000" })] })],
  });

  // RMD table rows
  const rmdTableRows = [];
  if (rmdResult && !rmdResult.notRequired) {
    const pairs = [
      ["Age at Year End", String(rmdResult.ageYE)],
      ["Distribution Divisor", rmdResult.divisor?.toFixed(1)],
      ["Regular IRA Total", usd(rmdResult.regularTotal)],
      ["Annuity FMV Total", usd(rmdResult.annuityFMVTotal)],
      ["Total FMV", usd(rmdResult.totalFMV)],
      ["Total Required RMD", usd(rmdResult.totalRMD)],
      ["Annuity Income", usd(rmdResult.annuityIncome), "059669"],
      ["Additional RMD Needed", usd(rmdResult.additionalRMD), rmdResult.additionalRMD > 0 ? "e11d48" : "059669"],
    ];
    pairs.forEach(([lbl, val, col]) => {
      rmdTableRows.push(new TableRow({ children: [
        new TableCell({ borders, width: { size: 4680, type: WidthType.DXA }, margins: cellM, shading: { fill: "F8FAFC", type: ShadingType.CLEAR }, children: [new Paragraph({ children: [new TextRun({ text: lbl, size: 20, font: "Arial", color: "475569" })] })] }),
        new TableCell({ borders, width: { size: 4680, type: WidthType.DXA }, margins: cellM, children: [new Paragraph({ children: [new TextRun({ text: val, size: 20, font: "Courier New", bold: true, color: col || "0f172a" })] })] }),
      ]}));
    });
  }

  // Projection table rows
  const projHeaderRow = new TableRow({ children: [
    hdrCell("Year", 1100), hdrCell("Age", 900), hdrCell("IRA Value", 1500),
    hdrCell("Divisor", 900), hdrCell("Total RMD", 1400),
    hdrCell("Ann. Income", 1500), hdrCell("Add'l RMD", 1560),
  ]});
  const projDataRows = (projData?.projections || []).map((r, i) => new TableRow({
    children: [
      dataCell(r.Year, 1100), dataCell(r.AgeYE, 900),
      dataCell(usd(r.NonAnnuityValue + r.AnnuityFMV), 1500),
      dataCell(r.Divisor.toFixed(1), 900),
      dataCell(usd(r.TotalRMD), 1400),
      dataCell(usd(r.AnnuityIncome), 1500),
      dataCell(usd(r.AdditionalRMD), 1560, r.AdditionalRMD > 0 ? "e11d48" : "059669"),
    ],
    tableHeader: false,
  }));

  const sp = (n) => new Paragraph({ children: [new TextRun("")], spacing: { after: n } });
  const ruled = new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: "1e3a5f", space: 1 } }, children: [] });

  const children = [
    // Logo centered
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new ImageRun({ data: logoBytes, transformation: { width: 100, height: 77 }, type: "jpg" })],
    }),
    sp(80),
    // Title
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: `Sec. 204 Calculations for ${clientName}`, bold: true, size: 32, font: "Arial", color: "1e3a5f" })],
    }),
    sp(40),
    ruled,
    sp(80),
    // Date + address block
    new Paragraph({ children: [new TextRun({ text: today, size: 22, font: "Arial", color: "475569" })] }),
    ...(client.contact_address
      ? client.contact_address.split("\n").map(line =>
          new Paragraph({ children: [new TextRun({ text: line, size: 22, font: "Arial", color: "475569" })] })
        )
      : []),
    sp(160),
    // RMD section header
    new Paragraph({ children: [new TextRun({ text: "RMD Calculation", bold: true, size: 26, font: "Arial", color: "1e3a5f" })] }),
    sp(80),
    ...(rmdTableRows.length
      ? [new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [4680, 4680], rows: rmdTableRows }), sp(160)]
      : [new Paragraph({ children: [new TextRun({ text: "RMD not calculated.", size: 20, font: "Arial", color: "94a3b8" })] }), sp(160)]
    ),
    // Projections header
    new Paragraph({ children: [new TextRun({ text: "10-Year Projections", bold: true, size: 26, font: "Arial", color: "1e3a5f" })] }),
    new Paragraph({ children: [new TextRun({ text: `Growth Rate: ${(growthRate * 100).toFixed(1)}%   |   Arbitrage Rate: ${(arbitrageRate * 100).toFixed(1)}%`, size: 18, font: "Arial", color: "64748b" })] }),
    sp(80),
    // Chart
    ...(chartBytes ? [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new ImageRun({ data: chartBytes, transformation: { width: 560, height: 222 }, type: "png" })],
      }),
      sp(120),
    ] : []),
    // Projection table
    ...(projDataRows.length
      ? [new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [1100, 900, 1500, 900, 1400, 1500, 1560], rows: [projHeaderRow, ...projDataRows] }), sp(160)]
      : [new Paragraph({ children: [new TextRun({ text: "Projections not calculated.", size: 20, font: "Arial", color: "94a3b8" })] }), sp(160)]
    ),
    // Disclaimer
    ruled,
    sp(80),
    new Paragraph({ children: [new TextRun({ text: "This report is for educational and planning purposes only. Actual RMD amounts depend on year-end account values, IRS table updates, and individual circumstances. Consult a qualified tax professional before making distribution decisions. Securities and advisory services offered through LPL Financial, a registered investment advisor, Member FINRA/SIPC. Section 204 Plan is a trademark and patent pending of Harness Financial, LLC.", size: 16, font: "Arial", color: "94a3b8" })] }),
  ];

  const doc = new Document({
    sections: [{
      properties: { page: { size: { width: 12240, height: 15840 }, margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 } } },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Sec204_${client.last_name}_${client.first_name}.docx`;
  a.click();
}


export { base64ToUint8Array, drawProjectionChart, downloadEML, downloadDOCX };
